import React, { useState, useEffect, ChangeEvent } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import {
  creditDistributor,
  fetchDistributorTransaction,
  fetchWalletFySummary,
  getDistributorById,
} from "../../features/distributor/DistributorSlice";
import { RootState } from "../../store/store";
import { Loader2, AlertCircle, ArrowLeft, Download, CheckCircle, X } from "lucide-react";
import { fetchBankList } from "../../features/program/ProgramSlice";
import CustomModal from "../common/CustomModal";
import AvailableWalletCard from "./AvailableWalletCard";
import DistributorTransactionModal from "./DistributorTransactionModal";
import PreviousFySummaryModal from "./PreviousFySummaryModal";
import YearEndModal from "./YearEnd";

/* --------------------------------- Interfaces ------------------------------ */

type Distributor = {
  distributorId: number;
  name: string;
};

interface TopUpPageProps {
  distributor: Distributor;
  onBack: () => void;
  onOpenAlertSettings?: (programId: number) => void;
}

const TopUpPage: React.FC<TopUpPageProps> = ({
  distributor,
  onBack,
  onOpenAlertSettings,
}) => {

  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch();
  const {
    loading,
    error,
    selectedDistributor,
    downloadingTransactions,
    selectedDistributorTransactions,
    selectedDistributorTransactionsLoading,
    selectedDistributorTransactionsError,
    walletFySummary,
    walletFySummaryLoading,
    walletFySummaryError,
  } = useAppSelector((state: RootState) => state.distributor);
  const { bankList } = useAppSelector((state: RootState) => state.program);

  /* --------------------------------- States ------------------------------ */

  const [success, setSuccess] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const [formData, setFormData] = useState({
    remark: "",
    amount: "",
    reference: "",
    bankId: "",
  });
  const [formErrors, setFormErrors] = useState({
    remark: "",
    amount: "",
    reference: "",
    bankId: "",
    programId: "",
  });
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedProgramNameForTransactions, setSelectedProgramNameForTransactions] = useState<string>("");
  const [selectedWalletIdForTransactions, setSelectedWalletIdForTransactions] = useState<number | null>(null);
  const [selectedProgramIdForTransactions, setSelectedProgramIdForTransactions] = useState<number | null>(null);
  const [showFySummaryModal, setShowFySummaryModal] = useState(false);
  const [selectedProgramNameForFySummary, setSelectedProgramNameForFySummary] = useState<string>("");
  const [selectedProgramIdForFySummary, setSelectedProgramIdForFySummary] = useState<string>("");
  const [yearEndModalProgramId, setYearEndModalProgramId] = useState<number | null>(null);
  const [closeFySuccessMessage, setCloseFySuccessMessage] = useState<string | null>(null);

  /* --------------------------------- Computed Values ------------------------------ */

  const selectedProgram = selectedDistributor?.wallet?.find(
    program => program.programId.toString() === selectedProgramId
  );

  const availablePrograms = selectedDistributor?.wallet || [];
  const activePrograms = availablePrograms.filter(
    program => program.ProgramDistributorWallet.isActive !== false
  );

  /* --------------------------------- Hooks ------------------------------ */

  useEffect(() => {
    if (success) {
      setShowTopUpModal(false);
      dispatch(getDistributorById(distributor.distributorId));
    }
  }, [success, distributor.distributorId, dispatch]);

  useEffect(() => {
    dispatch(fetchBankList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getDistributorById(distributor.distributorId));
  }, [dispatch, distributor.distributorId]);

  // Auto-select first active program if available
  useEffect(() => {
    if (activePrograms.length > 0 && !selectedProgramId) {
      setSelectedProgramId(activePrograms[0].programId.toString());
    }
  }, [activePrograms, selectedProgramId]);

  useEffect(() => {
    if (!closeFySuccessMessage) return;
    const t = setTimeout(() => setCloseFySuccessMessage(null), 5000);
    return () => clearTimeout(t);
  }, [closeFySuccessMessage]);

  /* ------------------------------ Event Handlers ---------------------------- */
  const handleChange = (e: any) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
    setFormErrors({ ...formErrors, [id]: "" });
  };

  const handleProgramChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedProgramId(e.target.value);
    setFormErrors({ ...formErrors, programId: "" });
  };

  const handleViewTransactions = (programId: number, programName: string) => {
    const program = availablePrograms.find((p) => p.programId === programId);
    const walletId = program?.ProgramDistributorWallet?.wallet_id || null;

    setSelectedProgramNameForTransactions(programName);
    setSelectedWalletIdForTransactions(walletId);
    setSelectedProgramIdForTransactions(programId);
    setShowTransactionModal(true);
    dispatch(
      fetchDistributorTransaction({
        distributorId: distributor.distributorId,
        programId: programId.toString(),
        page: 1,
        limit: 1000,
        sortBy: "createdAt",
        sortOrder: "DESC",
        forModal: true,
      })
    );
  };

  const handleCloseTransactionModal = () => {
    setShowTransactionModal(false);
    setSelectedProgramNameForTransactions("");
    setSelectedWalletIdForTransactions(null);
    setSelectedProgramIdForTransactions(null);
  };

  const handleRefreshTransactions = () => {
    if (selectedProgramIdForTransactions && distributor.distributorId) {
      dispatch(
        fetchDistributorTransaction({
          distributorId: distributor.distributorId,
          programId: selectedProgramIdForTransactions.toString(),
          page: 1,
          limit: 1000,
          sortBy: "createdAt",
          sortOrder: "DESC",
          forModal: true,
        })
      );
    }
  };

  const handlePreviousFySummary = (walletId: number, programName: string, programId: number) => {
    setSelectedProgramNameForFySummary(programName);
    setSelectedProgramIdForFySummary(programId.toString());
    setShowFySummaryModal(true);
    dispatch(
      fetchWalletFySummary({
        distributorId: distributor.distributorId,
        walletId,
      })
    );
  };

  const handleCloseFySummaryModal = () => {
    setShowFySummaryModal(false);
    setSelectedProgramNameForFySummary("");
    setSelectedProgramIdForFySummary("");
  };

  // Download transactions as CSV
  const downloadTransactions = async () => {
    try {
      const response = await dispatch(fetchDistributorTransaction({
        distributorId: distributor.distributorId,
        page: 1,
        limit: 10000, 
        sortBy: "createdAt",
        sortOrder: "DESC",
      }));

      if (fetchDistributorTransaction.fulfilled.match(response)) {
        const data = response.payload;
        
        if (!data.transactions || data.transactions.length === 0) {
          alert("No transactions found to download");
          return;
        }

        // Map transactions to specific fields
        const exportData = data.transactions.map((transaction: any) => {
          const date = transaction.transactionTypeDisplay === "debit" 
            ? transaction.debitDate 
            : transaction.transactionTypeDisplay === "settlement" 
            ? transaction.settlementDate 
            : transaction.createdAt;
          
          // Format date to YYYY-MM-DD (strips time and Excel recognizes as date)
          let formattedDate = "";
          if (date) {
            const dateObj = new Date(date);
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            formattedDate = `${year}-${month}-${day}`;
          }
          
          return {
            'Date Created': formattedDate,
            'Transaction Type': transaction.transactionTypeDisplay || "",
            'Top Up': transaction.topUpDelta || "",
            'Balance': transaction.balanceDelta || "",
            "Program": transaction["wallets.program.programName"] || "",
            'Bank': transaction['banks.displayName'] || "",
            'Bank Reference': transaction.transactionNumber || "",
            'Transaction Remark': transaction.remark || ""
          };
        });

        // Convert to CSV
        const headers = ['Date Created', 'Transaction Type', 'Top Up', 'Balance', 'Program', 'Bank', 'Bank Reference', 'Transaction Remark'];
        const csvContent = [
          headers.join(","),
          ...exportData.map(transaction => 
            headers.map(header => {
              const value = transaction[header as keyof typeof transaction];
              // Escape quotes and commas in values
              if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value ?? '';
            }).join(",")
          )
        ].join("\n");

        // Create and download file
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        const programName = selectedProgram?.programName || 'all_programs';
        const date = new Date().toISOString().split('T')[0];
        
        link.setAttribute("href", url);
        link.setAttribute("download", `${distributor.name}_${programName}_transactions_${date}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert("Failed to fetch transactions");
      }
    } catch (error) {
      console.error("Error downloading transactions:", error);
      alert("An error occurred while downloading transactions");
    }
  };

  const addBalance = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    let hasError = false;

    if (!selectedProgramId) {
      hasError = true;
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        programId: "Please select a program",
      }));
    }

    if (!formData.amount) {
      hasError = true;
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        amount: "Please enter valid amount",
      }));
    }

    if (!formData.reference) {
      hasError = true;
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        reference: "Please enter a valid bank reference",
      }));
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.reference)) {
      hasError = true;
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        reference: "Bank reference must contain only letters and numbers",
      }));
    }

    if (!formData.bankId) {
      hasError = true;
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        bankId: "Please select a bank",
      }));
    }

    if (hasError) return;

    setFormErrors({ remark: "", amount: "", reference: "", bankId: "", programId: "" });

    const response = await dispatch(
      creditDistributor({
        amount: formData.amount,
        distributorId: distributor.distributorId,
        remark: formData.remark,
        transactionNumber: formData.reference,
        bankId: formData.bankId,
        programId: selectedProgramId,
      }),
    );

    if (response.type === "distributor/creditDistributor/fulfilled") setSuccess(true);
  };

  /* ------------------------------ UI Handlers ---------------------------- */

  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Top Up Wallet
            </h1>
            <div className="text-sm text-gray-500 mt-1">
              {distributor.name}
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-4 md:mt-0">
          <button
            onClick={downloadTransactions}
            disabled={downloadingTransactions}
            className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {downloadingTransactions ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download Transactions
              </>
            )}
          </button>
          <button
            onClick={() => setShowTopUpModal(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/80 focus:outline-none"
          >
            Add Top-Up
          </button>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8">
        {closeFySuccessMessage && (
          <div className="mb-6 rounded-lg bg-green-50 border border-green-200 p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
              <CheckCircle className="w-5 h-5 shrink-0" />
              <span>{closeFySuccessMessage}</span>
            </div>
            <button
              type="button"
              onClick={() => setCloseFySuccessMessage(null)}
              className="p-1 rounded hover:bg-green-100 text-green-600 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex items-center gap-2 justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>{error ? error : "Something went wrong!"}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex justify-center items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Processing...</span>
            </div>
          </div>
        )}

        {/* Wallet Information Section */}
        {!loading && !error && selectedDistributor && (
          <div className="bg-white rounded-xl p-8 border-[1px] border-cardborder mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 ">Available Wallets</h2>

            {/* Available Wallets List */}
            <div className="space-y-8">
              {availablePrograms.map((program) => (
                <AvailableWalletCard
                  key={program.programId}
                  program={program}
                  onYearEndClick={setYearEndModalProgramId}
                  onViewTransactions={handleViewTransactions}
                  onPreviousFySummary={handlePreviousFySummary}
                  onAlertSettings={
                    onOpenAlertSettings
                      ? () => onOpenAlertSettings(program.programId)
                      : undefined
                  }
                />
              ))}
            </div>
          </div>
        )}

        <DistributorTransactionModal
          show={showTransactionModal}
          onHide={handleCloseTransactionModal}
          transactions={selectedDistributorTransactions?.transactions ?? null}
          loading={selectedDistributorTransactionsLoading}
          error={selectedDistributorTransactionsError}
          programName={selectedProgramNameForTransactions}
          walletId={selectedWalletIdForTransactions}
          distributorId={distributor.distributorId}
          programId={selectedProgramIdForTransactions}
          onRefreshTransactions={handleRefreshTransactions}
        />

        <PreviousFySummaryModal
          show={showFySummaryModal}
          onHide={handleCloseFySummaryModal}
          summaries={walletFySummary}
          loading={walletFySummaryLoading}
          error={walletFySummaryError}
          programName={selectedProgramNameForFySummary}
          distributorId={distributor.distributorId}
          programId={selectedProgramIdForFySummary}
        />

        {/* Form Section in Modal */}
        <CustomModal
          show={showTopUpModal}
          onHide={() => setShowTopUpModal(false)}
          title="Top Up Wallet"
          width="w-[500px]"
        >
          <form className="w-full space-y-4">
            {/* Program Selection */}
            <div>
              <label htmlFor="programId" className="block text-sm font-medium text-gray-700 mb-1">
                Select Program Wallet
              </label>
              <div className="relative">
                <select
                  id="programId"
                  value={selectedProgramId}
                  onChange={handleProgramChange}
                  className={`block w-full px-3 py-2 border ${formErrors.programId ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none`}
                >
                  <option value="">Select a program wallet</option>
                  {activePrograms.map((program) => (
                    <option key={program.programId} value={program.programId}>
                      {program.programName}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {formErrors.programId && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.programId}
                </div>
              )}
            </div>
            {/* Top Up Amount */}
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                Top Up Amount
              </label>
              <input
                id="amount"
                type="number"
                min={1}
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter Amount"
                className={`block w-full px-3 py-2 border ${formErrors.amount ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
              />
              {formErrors.amount && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.amount}
                </div>
              )}
            </div>
            {/* Bank Reference */}
            <div>
              <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-1">
                Bank Reference
              </label>
              <input
                id="reference"
                type="text"
                value={formData.reference}
                onChange={handleChange}
                placeholder="Transaction Reference No. as per Bank"
                className={`block w-full px-3 py-2 border ${formErrors.reference ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
              />
              {formErrors.reference && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.reference}
                </div>
              )}
            </div>
            {/* Select Bank */}
            <div>
              <label htmlFor="bankId" className="block text-sm font-medium text-gray-700 mb-1">
                Select Bank
              </label>
              <div className="relative">
                <select
                  id="bankId"
                  value={formData.bankId}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${formErrors.bankId ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none`}
                >
                  <option value="">Select a bank</option>
                  {bankList && bankList.map((bank) => (
                    <option key={bank.bankId} value={bank.bankId}>
                      {bank.displayName}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {formErrors.bankId && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.bankId}
                </div>
              )}
            </div>
            {/* Remark */}
            <div>
              <label htmlFor="remark" className="block text-sm font-medium text-gray-700 mb-1">
                Remark (Optional)
              </label>
              <input
                id="remark"
                type="text"
                value={formData.remark}
                onChange={handleChange}
                placeholder="Optional Remark"
                className={`block w-full px-3 py-2 border ${formErrors.remark ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none`}
              />
              {formErrors.remark && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.remark}
                </div>
              )}
            </div>
            {/* Submit Button */}
            <div className="flex justify-between pt-2">
              <button
                type="button"
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors"
                onClick={() => setShowTopUpModal(false)}
              >
                Cancel
              </button>
              <button
                onClick={addBalance}
                className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </div>
                ) : (
                  "Top Up Wallet"
                )}
              </button>
            </div>
          </form>
        </CustomModal>

        <YearEndModal
          show={yearEndModalProgramId !== null}
          onHide={() => setYearEndModalProgramId(null)}
          program={availablePrograms.find((p) => p.programId === yearEndModalProgramId) ?? null}
          onCloseFySuccess={() => {
            dispatch(getDistributorById(distributor.distributorId));
            setCloseFySuccessMessage("Financial year closed successfully.");
          }}
        />
      </div>
    </div>
  );
};

export default TopUpPage;
